import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  UserCircle,
  Palette,
  Link2,
  Image,
  Upload,
  Save,
  RefreshCw,
  ExternalLink,
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Copy,
  Check,
  User,
  FileText,
  Pencil
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OrganizationProfile() {
  const { user, token, refreshUser } = useAuth();
  const logoInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const [profile, setProfile] = useState({
    organization_name: '',
    representative_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    website: '',
    description: ''
  });
  
  const [editedProfile, setEditedProfile] = useState({...profile});
  
  const [branding, setBranding] = useState({
    org_slug: '',
    logo_url: '',
    brand_color: '#84cc16'
  });

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [brandingRes, profileRes] = await Promise.all([
        axios.get(`${API}/api/organization/branding`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/organization/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} }))
      ]);
      
      setBranding({
        org_slug: brandingRes.data.org_slug || '',
        logo_url: brandingRes.data.logo_url || '',
        brand_color: brandingRes.data.brand_color || '#84cc16'
      });
      
      const profileData = {
        organization_name: profileRes.data.organization_name || brandingRes.data.organization_name || user?.organization_name || user?.name || '',
        representative_name: profileRes.data.representative_name || user?.representative_name || '',
        email: profileRes.data.email || user?.email || '',
        phone: profileRes.data.phone || user?.phone || '',
        address: profileRes.data.address || user?.address || '',
        city: profileRes.data.city || user?.city || '',
        country: profileRes.data.country || user?.country || '',
        website: profileRes.data.website || user?.website || '',
        description: profileRes.data.description || user?.description || ''
      };
      
      setProfile(profileData);
      setEditedProfile(profileData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/api/organization/profile`, editedProfile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(editedProfile);
      setIsEditing(false);
      toast.success('Profile updated successfully!');
      // Refresh user data to update sidebar
      if (refreshUser) {
        refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditedProfile(profile);
    setIsEditing(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/api/organization/upload-logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setBranding({ ...branding, logo_url: response.data.logo_url });
      toast.success('Logo uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    }
  };

  const copyBrandedUrl = () => {
    const url = `${window.location.origin}/org-login/${branding.org_slug}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Branded login URL copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <DashboardLayout role="organization">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-lime-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="organization">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl">Organization Profile</h2>
            <p className="text-muted-foreground">Manage your organization's profile information</p>
          </div>
          {!isEditing ? (
            <Button onClick={() => setIsEditing(true)} variant="outline">
              <Pencil className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={cancelEdit} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={saveProfile} disabled={saving} className="bg-lime-500 hover:bg-lime-600">
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Profile Picture / Logo Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5 text-lime-500" />
                Organization Logo
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center space-y-4">
              {branding.logo_url ? (
                <div className="w-32 h-32 rounded-lg border bg-white p-2 flex items-center justify-center">
                  <img 
                    src={branding.logo_url} 
                    alt={profile.organization_name}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div 
                  className="w-32 h-32 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: branding.brand_color }}
                >
                  <Building2 className="w-16 h-16 text-white" />
                </div>
              )}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <Button variant="outline" onClick={() => logoInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" />
                {branding.logo_url ? 'Change Logo' : 'Upload Logo'}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                PNG, JPG up to 2MB<br/>Recommended: 200x200px
              </p>
            </CardContent>
          </Card>

          {/* Basic Information */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-lime-500" />
                Basic Information
              </CardTitle>
              <CardDescription>Your organization's primary details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="organization_name">Organization Name *</Label>
                  {isEditing ? (
                    <Input
                      id="organization_name"
                      value={editedProfile.organization_name}
                      onChange={(e) => setEditedProfile({...editedProfile, organization_name: e.target.value})}
                      placeholder="University of Example"
                    />
                  ) : (
                    <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md min-h-[40px]">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{profile.organization_name || '-'}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="representative_name">Representative Name</Label>
                  {isEditing ? (
                    <Input
                      id="representative_name"
                      value={editedProfile.representative_name}
                      onChange={(e) => setEditedProfile({...editedProfile, representative_name: e.target.value})}
                      placeholder="John Smith"
                    />
                  ) : (
                    <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md min-h-[40px]">
                      <User className="w-4 h-4 text-muted-foreground" />
                      <span>{profile.representative_name || '-'}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  {isEditing ? (
                    <Input
                      id="email"
                      type="email"
                      value={editedProfile.email}
                      onChange={(e) => setEditedProfile({...editedProfile, email: e.target.value})}
                      placeholder="contact@university.edu"
                    />
                  ) : (
                    <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md min-h-[40px]">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span>{profile.email || '-'}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  {isEditing ? (
                    <Input
                      id="phone"
                      value={editedProfile.phone}
                      onChange={(e) => setEditedProfile({...editedProfile, phone: e.target.value})}
                      placeholder="+44 123 456 7890"
                    />
                  ) : (
                    <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md min-h-[40px]">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span>{profile.phone || '-'}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  {isEditing ? (
                    <Input
                      id="website"
                      value={editedProfile.website}
                      onChange={(e) => setEditedProfile({...editedProfile, website: e.target.value})}
                      placeholder="https://www.university.edu"
                    />
                  ) : (
                    <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md min-h-[40px]">
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      {profile.website ? (
                        <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {profile.website}
                        </a>
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Address Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-lime-500" />
              Address Information
            </CardTitle>
            <CardDescription>Your organization's location details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="address">Street Address</Label>
                {isEditing ? (
                  <Input
                    id="address"
                    value={editedProfile.address}
                    onChange={(e) => setEditedProfile({...editedProfile, address: e.target.value})}
                    placeholder="123 University Street"
                  />
                ) : (
                  <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md min-h-[40px]">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{profile.address || '-'}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                {isEditing ? (
                  <Input
                    id="city"
                    value={editedProfile.city}
                    onChange={(e) => setEditedProfile({...editedProfile, city: e.target.value})}
                    placeholder="London"
                  />
                ) : (
                  <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md min-h-[40px]">
                    <span>{profile.city || '-'}</span>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                {isEditing ? (
                  <Input
                    id="country"
                    value={editedProfile.country}
                    onChange={(e) => setEditedProfile({...editedProfile, country: e.target.value})}
                    placeholder="United Kingdom"
                  />
                ) : (
                  <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md min-h-[40px]">
                    <span>{profile.country || '-'}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Description */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-lime-500" />
              About Organization
            </CardTitle>
            <CardDescription>A brief description of your organization</CardDescription>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <Textarea
                value={editedProfile.description}
                onChange={(e) => setEditedProfile({...editedProfile, description: e.target.value})}
                placeholder="Tell us about your organization, its mission, and values..."
                rows={4}
              />
            ) : (
              <div className="p-3 bg-muted/50 rounded-md min-h-[100px]">
                <p className="text-sm">{profile.description || 'No description provided.'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Branded Login URL Card */}
        {branding.org_slug && (
          <Card className="border-lime-500/30 bg-lime-500/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-lime-500" />
                Your Branded Login Page
              </CardTitle>
              <CardDescription>
                Share this link with your students and agents for a branded login experience
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Input
                  value={`${window.location.origin}/org-login/${branding.org_slug}`}
                  readOnly
                  className="bg-background"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyBrandedUrl}
                >
                  {copied ? <Check className="w-4 h-4 text-lime-500" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => window.open(`/org-login/${branding.org_slug}`, '_blank')}
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                This page displays your logo and branding. All emails sent through your organization will include this link.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
